import SimpleTooltip from '@rio-cloud/rio-uikit/components/tooltip/SimpleTooltip';
import Tooltip from '@rio-cloud/rio-uikit/components/tooltip/Tooltip';
import gt from 'lodash/fp/gt';
import isNil from 'lodash/fp/isNil';
import isNull from 'lodash/fp/isNull';
import { FormattedMessage, useIntl } from 'react-intl';
import ManChargeAndGoLogo from '../../../assets/logo/MAN_Charge_And_Go.svg?react';
import { Unit } from '../../../common/constants';
import {
    convertISOToRelativeDate,
    convertSecondsIntoHHmmDisplay,
    getBatteryColor,
    getBatteryIcon,
} from '../../../common/utils';
import { RangeStatusIcon } from '../../../components/RangeStatusIcon/RangeStatusIcon';

export type ChargingInfo = {
    arrivalTime?: string;
    duration?: number;
    operatorName?: string;
    address?: string;
    isInRange?: boolean;
    arrivalBatteryPct?: number;
    departBatteryPct?: number;
    hasTCScontract?: boolean;
};

export const ChargingPanel = ({
    arrivalTime,
    duration,
    operatorName,
    address,
    isInRange,
    arrivalBatteryPct,
    departBatteryPct,
    hasTCScontract,
}: ChargingInfo) => {
    const intl = useIntl();

    const date = convertISOToRelativeDate(intl.locale, arrivalTime);

    return (
        <li className='timeline-element' data-testid='timeline-element'>
            <div className='timeline-date margin-right-10'>
                <div className='border border-width-3 border-color-white'>
                    <span className='badge badge-lg badge-muted padding-5'>
                        <span className='rioglyph rioglyph-filling-e-station' />
                    </span>
                </div>
            </div>
            <div className='timeline-content margin-bottom-15'>
                <div className='text-medium text-size-16 text-color-darker margin-top-5 margin-bottom-5'>
                    <span className='margin-right-5'>
                        <FormattedMessage id='intl-msg:smartRoute.tour.chargingStops' />
                    </span>
                    {!isNull(isInRange) && (
                        <RangeStatusIcon isWaypointInRange={isInRange ?? false} badgeSize='badge-sm' />
                    )}
                </div>
                <div className='display-flex flex-column gap-2'>
                    {gt(duration, 0) && (
                        <div>
                            <span className='rioglyph rioglyph-time text-color-gray margin-right-5' />
                            {!isNil(duration) ? <span>{convertSecondsIntoHHmmDisplay(duration)}</span> : '-'}
                        </div>
                    )}

                    <div>
                        <span className='rioglyph rioglyph-calendar text-color-gray margin-right-5' />
                        <span>{date}</span>
                    </div>

                    {!isNil(operatorName) && (
                        <div>
                            <span className='rioglyph rioglyph-addressbook text-color-gray margin-right-5' />
                            <span>{operatorName}</span>
                        </div>
                    )}

                    <div>
                        <span className='rioglyph rioglyph-map-marker text-color-gray margin-right-5' />
                        <span>{address}</span>
                    </div>

                    {!isNil(arrivalBatteryPct) && !isNil(departBatteryPct) && (
                        <div className='display-flex align-items-center'>
                            <div className={`${getBatteryColor(arrivalBatteryPct)}`}>
                                <span className={`${getBatteryIcon(arrivalBatteryPct)} margin-right-5`} />
                                <span>{arrivalBatteryPct}</span>
                                <span>&nbsp;{Unit.pct}</span>
                            </div>
                            <span className='rioglyph rioglyph-arrow-right text-color-gray margin-x-10' />
                            <div className={`${getBatteryColor(departBatteryPct)}`}>
                                <span>{departBatteryPct}</span>
                                <span>&nbsp;{Unit.pct}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {hasTCScontract && (
                <SimpleTooltip
                    placement={Tooltip.BOTTOM}
                    content={intl.formatMessage({ id: 'intl-msg:smartRoute.chargingStation.c&g.tooltip' })}
                >
                    <div className='position-absolute right-15'>
                        <ManChargeAndGoLogo />
                    </div>
                </SimpleTooltip>
            )}
        </li>
    );
};
